import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { LogoFull } from "@/components/core/Logo";
import { errMsg } from "@/lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Bentornato, ${u.nome}!`);
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err, "Accesso non riuscito"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="core-public min-h-screen bg-[#7CC6FA] relative overflow-hidden flex flex-col" data-testid="login-page">
      <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-white/15" />
      <div className="absolute -left-24 bottom-[-180px] h-[420px] w-[420px] rounded-full border-[56px] border-white/20" />
      <div className="relative flex-1 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] items-center gap-8 lg:gap-12 px-4 sm:px-10 lg:px-20 py-8 lg:py-14 max-w-[1500px] w-full mx-auto">
        <div className="core-rise">
          <LogoFull className="w-full max-w-[620px]" />
          <p className="text-[#0A1128]/80 mt-10 max-w-md text-base font-medium">
            Lo spazio di lavoro interno dell'agenzia: clienti, task, documenti, calendari e piano editoriale in un unico posto.
          </p>
        </div>
        <form onSubmit={submit} className="w-full max-w-md justify-self-center lg:justify-self-end bg-[#A6DAFC] rounded-3xl p-6 sm:p-10 shadow-[0_24px_60px_rgba(10,17,40,0.18)] core-rise core-delay-2" data-testid="login-form">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8173A] mb-2">Area riservata ai soci</div>
          <h2 className="font-display text-3xl font-extrabold text-[#0A1128]">Accedi</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Inserisci le tue credenziali per continuare.</p>
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500">Email o username</Label>
              <Input id="email" type="text" required autoComplete="username" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl" placeholder="nome@agenzia.it oppure nome.cognome" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500">Password</Label>
              <Input id="password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl" placeholder="••••••••" data-testid="login-password-input" />
            </div>
            {error && <div className="text-sm font-semibold text-[#C8102E] bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="login-error">{error}</div>}
            <div className="text-right -mt-2">
              <Link to="/password-dimenticata" className="text-sm font-bold text-[#0A1128] hover:text-[#E8173A]" data-testid="forgot-password-link">Password dimenticata?</Link>
            </div>
            <button type="submit" disabled={loading}
              className="group w-full h-12 rounded-full bg-[#0A1128] hover:bg-[#E8173A] text-white font-bold flex items-center justify-center gap-2 transition-colors duration-300 disabled:opacity-70"
              data-testid="login-submit-button">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Accedi <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
            </button>
          </div>
        </form>
      </div>
      <div className="relative text-[#0A1128]/60 text-xs px-4 sm:px-10 lg:px-20 pb-6">© {new Date().getFullYear()} core. – the heart of your business</div>
    </div>
  );
}
